import React, { useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    Grid,
    Alert,
    CircularProgress,
    Chip,
} from '@mui/material';
import { mlAnalyticsAPI } from '../../services/mlAnalyticsApi';

const TestPrediction: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    const [formData, setFormData] = useState({
        student_id: 'TEST001',
        student_name: 'Test Student',
        school_id: 'SCH001',
        school_name: 'Test School',
        attendance_last_30_days: '0.75',
        attendance_last_60_days: '0.78',
        attendance_last_90_days: '0.80',
        failed_subjects_count: '2',
        max_consecutive_absences: '5',
        current_gpa: '2.5',
    });

    const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [field]: event.target.value });
    };

    const handlePredict = async () => {
        try {
            setLoading(true);
            setError(null);
            setResult(null);

            const studentData = {
                ...formData,
                attendance_last_30_days: parseFloat(formData.attendance_last_30_days),
                attendance_last_60_days: parseFloat(formData.attendance_last_60_days),
                attendance_last_90_days: parseFloat(formData.attendance_last_90_days),
                failed_subjects_count: parseInt(formData.failed_subjects_count),
                max_consecutive_absences: parseInt(formData.max_consecutive_absences),
                current_gpa: parseFloat(formData.current_gpa),
            };

            const response = await mlAnalyticsAPI.predictDropoutRisk(studentData);

            if (response.data.status === 'success') {
                setResult(response.data.data[0]);
            } else {
                setError('Prediction failed');
            }
        } catch (err: any) {
            console.error('Prediction error:', err);
            setError(err.response?.data?.message || 'Failed to make prediction');
        } finally {
            setLoading(false);
        }
    };

    const getRiskColor = (level: string) => {
        switch (level) {
            case 'critical':
                return 'error';
            case 'high':
                return 'warning';
            case 'medium':
                return 'info';
            case 'low':
                return 'success';
            default:
                return 'default';
        }
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Test Dropout Risk Prediction
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
                Enter student data to test the dropout risk prediction model
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Student Information
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Student ID"
                                        value={formData.student_id}
                                        onChange={handleChange('student_id')}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Student Name"
                                        value={formData.student_name}
                                        onChange={handleChange('student_name')}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="School ID"
                                        value={formData.school_id}
                                        onChange={handleChange('school_id')}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="School Name"
                                        value={formData.school_name}
                                        onChange={handleChange('school_name')}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Attendance Rate (30 days)"
                                        type="number"
                                        value={formData.attendance_last_30_days}
                                        onChange={handleChange('attendance_last_30_days')}
                                        inputProps={{ min: 0, max: 1, step: 0.01 }}
                                        helperText="0.0 to 1.0 (e.g., 0.75 = 75%)"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Attendance Rate (60 days)"
                                        type="number"
                                        value={formData.attendance_last_60_days}
                                        onChange={handleChange('attendance_last_60_days')}
                                        inputProps={{ min: 0, max: 1, step: 0.01 }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Attendance Rate (90 days)"
                                        type="number"
                                        value={formData.attendance_last_90_days}
                                        onChange={handleChange('attendance_last_90_days')}
                                        inputProps={{ min: 0, max: 1, step: 0.01 }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Failed Subjects Count"
                                        type="number"
                                        value={formData.failed_subjects_count}
                                        onChange={handleChange('failed_subjects_count')}
                                        inputProps={{ min: 0 }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Max Consecutive Absences"
                                        type="number"
                                        value={formData.max_consecutive_absences}
                                        onChange={handleChange('max_consecutive_absences')}
                                        inputProps={{ min: 0 }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Current GPA"
                                        type="number"
                                        value={formData.current_gpa}
                                        onChange={handleChange('current_gpa')}
                                        inputProps={{ min: 0, max: 4, step: 0.1 }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        onClick={handlePredict}
                                        disabled={loading}
                                    >
                                        {loading ? <CircularProgress size={24} /> : 'Predict Dropout Risk'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Prediction Results
                            </Typography>

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            {result ? (
                                <Box>
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="body2" color="textSecondary" gutterBottom>
                                            Risk Score
                                        </Typography>
                                        <Typography variant="h2" color={getRiskColor(result.risk_level)}>
                                            {result.risk_score.toFixed(1)}%
                                        </Typography>
                                    </Box>

                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="body2" color="textSecondary" gutterBottom>
                                            Risk Level
                                        </Typography>
                                        <Chip
                                            label={result.risk_level.toUpperCase()}
                                            color={getRiskColor(result.risk_level)}
                                            size="medium"
                                            sx={{ fontSize: '1rem', fontWeight: 'bold', py: 2.5 }}
                                        />
                                    </Box>

                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="body2" color="textSecondary" gutterBottom>
                                            Confidence Score
                                        </Typography>
                                        <Typography variant="h6">
                                            {(result.confidence_score * 100).toFixed(0)}%
                                        </Typography>
                                    </Box>

                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="body2" color="textSecondary" gutterBottom>
                                            Contributing Factors
                                        </Typography>
                                        {result.contributing_factors.map((factor: string, index: number) => (
                                            <Chip
                                                key={index}
                                                label={factor}
                                                size="small"
                                                sx={{ mr: 0.5, mb: 0.5 }}
                                            />
                                        ))}
                                    </Box>

                                    {result.interventions && result.interventions.length > 0 && (
                                        <Box>
                                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                                Recommended Interventions
                                            </Typography>
                                            {result.interventions.map((intervention: any, index: number) => (
                                                <Alert key={index} severity="info" sx={{ mb: 1 }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {intervention.intervention_type_display}
                                                    </Typography>
                                                    <Typography variant="caption">
                                                        {intervention.description}
                                                    </Typography>
                                                </Alert>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Alert severity="info">
                                    Enter student data and click "Predict Dropout Risk" to see results
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default TestPrediction;
